import React, { useState } from 'react'
import Spinner from 'react-bootstrap/Spinner'

const HatstatsHistoryChart = ({ history }) => {
  const width = 760
  const height = 190
  const padding = { top: 18, right: 16, bottom: 28, left: 42 }
  const values = history.map(point => point.hatstats)
  const minValue = Math.min(...values)
  const maxValue = Math.max(...values)
  const valueRange = maxValue - minValue || 1
  const chartWidth = width - padding.left - padding.right
  const chartHeight = height - padding.top - padding.bottom
  const points = history.map((point, index) => {
    const x = padding.left + (history.length === 1 ? chartWidth / 2 : (index / (history.length - 1)) * chartWidth)
    const y = padding.top + ((maxValue - point.hatstats) / valueRange) * chartHeight
    return { ...point, x, y }
  })
  const linePoints = points.map(point => `${point.x},${point.y}`).join(' ')
  const formatDate = value => new Date(`${value}`.replace(' ', 'T')).toLocaleDateString('fi-FI')

  return (
    <div className="hatstatsHistory" aria-label="Competitive match HatStats history">
      <div className="hatstatsHistoryTitle">HatStats history — competitive matches</div>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="HatStats line chart">
        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={height - padding.bottom} className="hatstatsAxis" />
        <line x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} className="hatstatsAxis" />
        <text x={padding.left - 6} y={padding.top + 4} textAnchor="end" className="hatstatsAxisLabel">{maxValue}</text>
        <text x={padding.left - 6} y={height - padding.bottom + 4} textAnchor="end" className="hatstatsAxisLabel">{minValue}</text>
        <polyline points={linePoints} fill="none" className="hatstatsLine" />
        {points.map(point => (
          point.matchId ? (
            <a
              key={point.matchId}
              href={`https://hattrick.org/goto.ashx?path=/Club/Matches/Match.aspx?matchID=${point.matchId}`}
              target="_blank"
              rel="noreferrer"
            >
              <circle cx={point.x} cy={point.y} r="4" className="hatstatsPoint">
                <title>{`${formatDate(point.matchDate)}: ${point.hatstats}`}</title>
              </circle>
            </a>
          ) : (
            <circle key={point.matchDate} cx={point.x} cy={point.y} r="4" className="hatstatsPoint">
              <title>{`${formatDate(point.matchDate)}: ${point.hatstats}`}</title>
            </circle>
          )
        ))}
        <text x={padding.left} y={height - 7} className="hatstatsAxisLabel">{formatDate(history[0].matchDate)}</text>
        <text x={width - padding.right} y={height - 7} textAnchor="end" className="hatstatsAxisLabel">{formatDate(history[history.length - 1].matchDate)}</text>
      </svg>
    </div>
  )
}

const formatOneDecimal = value => {
  return Number(value).toFixed(1).replace('.', ',')
}

const formatSignedDelta = value => {
  const numericValue = Number(value)
  const prefix = numericValue > 0 ? '+' : numericValue < 0 ? '-' : ''

  return `${prefix}${formatOneDecimal(Math.abs(numericValue))}`
}

const TeamsTable = ({
  isTeamsLoading,
  seriesControls,
  columns,
  tableMode,
  sortConfig,
  databaseSearchDirections,
  showDatabaseSearchRow,
  getColumnClassName,
  toggleSort,
  onDatabaseSearch,
  renderColumnLabel,
  getSortArrow,
  teamRows,
  isOwnRow,
  favoriteTeamIds,
  isAuthenticated,
  ownRowSpecialClassName,
  toggleFavoriteSelection,
}) => {
  const [expandedHatstatsTeamId, setExpandedHatstatsTeamId] = useState(null)
  const renderStatValue = (team, key) => {
    const value = team[key]
    const deltaKey = key === 'avgStamina' ? 'avgStaminaDelta' : 'avgFormDelta'
    const delta = team[deltaKey]

    if (!Number.isFinite(Number(value))) {
      return value ?? '-'
    }

    return (
      <span className="statValueWithTrend">
        <span>{formatOneDecimal(value)}</span>
        {Number.isFinite(Number(delta)) && Number(delta) !== 0 ? (
          <span className={`statTrend ${Number(delta) > 0 ? 'statTrendPositive' : 'statTrendNegative'}`}>
            {formatSignedDelta(delta)}
          </span>
        ) : null}
      </span>
    )
  }

  const renderSearchButtons = column => {
    const searchButtons = Array.isArray(column.searchButtons) ? column.searchButtons : []

    if (!searchButtons.length && !column.searchKey) {
      return null
    }

    const targets = searchButtons.length
      ? searchButtons
      : [{ key: column.searchKey, label: column.searchLabel || column.label }]

    if (column.key === 'coach') {
      return (
        <div className="searchButtonGroup coachSearchGroup">
          <div className="coachSearchPill">
            {targets.map(target => {
              const nextDirection = databaseSearchDirections?.[target.key] || 'desc'
              const isDescending = nextDirection === 'asc'

              return (
                <button
                  key={target.key}
                  type="button"
                  className="headerSearchButton coachSearchButton"
                  onClick={() => onDatabaseSearch(target.key)}
                  title={isDescending ? `Search worst ${target.label.toLowerCase()}` : `Search best ${target.label.toLowerCase()}`}
                  aria-label={isDescending ? `Search worst ${target.label.toLowerCase()}` : `Search best ${target.label.toLowerCase()}`}
                >
                  <span className={isDescending ? 'searchArrowDown' : 'searchArrowUp'}>
                    ▲
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )
    }

    return (
      <div className="searchButtonGroup">
        {targets.map(target => (
          <span key={target.key} className="searchButtonPair">
            {(() => {
              const nextDirection = databaseSearchDirections?.[target.key] || 'desc'
              const isDescending = nextDirection === 'asc'

              return (
                <button
                  type="button"
                  className="headerSearchButton"
                  onClick={() => onDatabaseSearch(target.key)}
                  title={isDescending ? `Search worst ${target.label.toLowerCase()}` : `Search best ${target.label.toLowerCase()}`}
                  aria-label={isDescending ? `Search worst ${target.label.toLowerCase()}` : `Search best ${target.label.toLowerCase()}`}
                >
                  <span className={isDescending ? 'searchArrowDown' : 'searchArrowUp'}>
                    ▲
                  </span>
                </button>
              )
            })()}
          </span>
        ))}
      </div>
    )
  }

  return (
    <section className="tableWrap">
      {seriesControls ? <div className="tableSeriesPanel">{seriesControls}</div> : null}
      {isTeamsLoading ? (
        <div className="loadingState" role="status" aria-live="polite">
          <Spinner animation="border" variant="light" />
          <span>Loading data...</span>
        </div>
      ) : (
        <table>
          <thead>
            {showDatabaseSearchRow ? (
              <tr className="searchRow">
                {columns.map(column => (
                  <th key={`${column.key}-search`} className={getColumnClassName(column.key)}>
                    {renderSearchButtons(column)}
                  </th>
                ))}
              </tr>
            ) : null}
            <tr>
              {columns.map(column => (
                <th key={column.key} className={getColumnClassName(column.key)}>
                  {column.sortKey ? (
                    <button
                      type="button"
                      className="headerSortButton"
                      onClick={() => toggleSort(column.sortKey)}
                    >
                      {renderColumnLabel(column)}
                      <span className="sortArrow">{getSortArrow(column.sortKey)}</span>
                    </button>
                  ) : (
                    renderColumnLabel(column)
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {teamRows.map((team, index) => {
              const rowKey = team.rowId || team.TeamID
              const hasHatstatsHistory = Array.isArray(team.hatstatsHistory) && team.hatstatsHistory.length > 0
              const isHatstatsHistoryExpanded = expandedHatstatsTeamId === rowKey

              return (
              <React.Fragment key={rowKey}>
              <tr
                className={
                  isOwnRow(team)
                    ? `ownTeamRow ${ownRowSpecialClassName || ''}`.trim()
                    : ''
                }
              >
                {columns.map(column => (
                  <td key={`${team.rowId || team.TeamID}-${column.key}`} className={getColumnClassName(column.key)}>
                    {column.key === 'discordSelect' ? (
                      team.isAverageTeam ? (
                        '-'
                      ) : (
                      <input
                        type="checkbox"
                        checked={favoriteTeamIds.includes(Number(team.TeamID))}
                        disabled={!isAuthenticated}
                        onChange={() => toggleFavoriteSelection(Number(team.TeamID))}
                        aria-label={`Toggle favorite for ${team.teamName || team.TeamID}`}
                      />
                      )
                    ) : column.key === 'position' ? (
                      index + 1
                    ) : column.key === 'user' ? (
                      team.isAverageTeam ? '-' : (
                      <div className="userCell">
                        {team.userFlagCode ? (
                          team.userCountryUrl ? (
                            <a
                              href={team.userCountryUrl}
                              target="_blank"
                              rel="noreferrer"
                              aria-label={`${team.userLabel} country`}
                            >
                              <img
                                src={`https://flagcdn.com/20x15/${team.userFlagCode}.png`}
                                alt={team.userLabel}
                                width="20"
                                height="15"
                              />
                            </a>
                          ) : (
                            <img
                              src={`https://flagcdn.com/20x15/${team.userFlagCode}.png`}
                              alt={team.userLabel}
                              width="20"
                              height="15"
                            />
                          )
                        ) : null}
                        {team.userUrl ? (
                          <a
                            href={team.userUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="userNameLink"
                            title={team.userLabel}
                          >
                            {team.userShortLabel}
                          </a>
                        ) : (
                          <span title={team.userLabel}>{team.userShortLabel}</span>
                        )}
                      </div>
                      )
                    ) : column.key === 'teamName' ? (
                      <div className="userCell">
                        {team.flagCode && team.countryLeagueUrl ? (
                          <a href={team.countryLeagueUrl} target="_blank" rel="noreferrer">
                            <img
                              src={`https://flagcdn.com/20x15/${team.flagCode}.png`}
                              alt={team?.FemmeCountryName || 'flag'}
                              width="20"
                              height="15"
                            />
                          </a>
                        ) : team.flagCode ? (
                          <img
                            src={`https://flagcdn.com/20x15/${team.flagCode}.png`}
                            alt={team?.FemmeCountryName || 'flag'}
                            width="20"
                            height="15"
                          />
                        ) : null}
                        {team.teamUrl ? (
                          <a href={team.teamUrl} target="_blank" rel="noreferrer">
                            {team.teamName}
                          </a>
                        ) : (
                          team.teamName
                        )}
                      </div>
                    ) : column.key === 'cup' ? (
                      team.cupIcon ? (
                        team.cupMatchUrl ? (
                          <a
                            href={team.cupMatchUrl}
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Open upcoming cup match"
                          >
                            <img
                              src={team.cupIcon}
                              alt="Cup status"
                              width="12"
                              height="13"
                              className="cupStatusIcon"
                            />
                          </a>
                        ) : (
                          <img
                            src={team.cupIcon}
                            alt="Cup status"
                            width="12"
                            height="13"
                            className="cupStatusIcon"
                          />
                        )
                      ) : (
                        '-'
                      )
                    ) : column.key === 'coach' ? (
                      team.coachUrl && team.coach && team.coach !== '-' ? (
                        <a href={team.coachUrl} target="_blank" rel="noreferrer" aria-label="Coach details">
                          {team.coach}
                        </a>
                      ) : (
                        team.coach ?? '-'
                      )
                    ) : column.key === 'youth' ? (
                      tableMode === 'team' ? (
                        team.youthDisplay === 'team' && team.youthTeamUrl ? (
                          <a href={team.youthTeamUrl} target="_blank" rel="noreferrer" aria-label="Youth team">
                            {team.youthTeamModeLabel}
                          </a>
                        ) : (
                          team.youthTeamModeLabel || '-'
                        )
                      ) : team.youthTopSalary !== null && team.youthTopSalary !== undefined ? (
                        team.youthTopSalaryUrl ? (
                          <a href={team.youthTopSalaryUrl} target="_blank" rel="noreferrer" aria-label="Top youth salary player">
                            {team.youthTopSalary}
                          </a>
                        ) : (
                          team.youthTopSalary
                        )
                      ) : (
                        '-'
                      )
                    ) : column.key === 'arenaCapacity' ? (
                      team.arenaCapacityTooltip && team.arenaUrl ? (
                        <a
                          href={team.arenaUrl}
                          target="_blank"
                          rel="noreferrer"
                          title={team.arenaCapacityTooltip}
                          aria-label={team.arenaCapacityTooltip}
                        >
                          {team.arenaCapacity}
                        </a>
                      ) : team.arenaCapacityTooltip ? (
                        <span title={team.arenaCapacityTooltip} aria-label={team.arenaCapacityTooltip}>
                          {team.arenaCapacity}
                        </span>
                      ) : team.arenaUrl ? (
                        <a href={team.arenaUrl} target="_blank" rel="noreferrer">
                          {team.arenaCapacity}
                        </a>
                      ) : (
                        team.arenaCapacity ?? '-'
                      )
                    ) : column.key === 'specialtyCount' ? (
                      team.specialtyCountDisplay ?? team.specialtyCount ?? '-'
                    ) : column.key === 'hatstats' ? (
                      <span className="hatstatsCell">
                        {team.hatstatsMatchUrl && Number.isFinite(Number(team.hatstats)) && Number(team.hatstats) > 0 ? (
                          <a href={team.hatstatsMatchUrl} target="_blank" rel="noreferrer" aria-label="Open best hatstats match">
                            {team.hatstats}
                          </a>
                        ) : (
                          team.hatstats ?? '-'
                        )}
                        {hasHatstatsHistory ? (
                          <button
                            type="button"
                            className="hatstatsHistoryToggle"
                            onClick={() => setExpandedHatstatsTeamId(isHatstatsHistoryExpanded ? null : rowKey)}
                            aria-label="Toggle HatStats history"
                            aria-expanded={isHatstatsHistoryExpanded}
                            title="Show HatStats history"
                          >
                            {'\u{1F4C8}'}
                          </button>
                        ) : null}
                      </span>
                    ) : column.key === 'remainingCupSeed' ? (
                      team.cupUrl && team.remainingCupSeed !== '-' ? (
                        <a href={team.cupUrl} target="_blank" rel="noreferrer" aria-label="Open team cup page">
                          {team.remainingCupSeed}
                        </a>
                      ) : (
                        team.remainingCupSeed ?? '-'
                      )
                    ) : column.key === 'series' ? (
                      team.seriesUrl ? (
                        <a href={team.seriesUrl} target="_blank" rel="noreferrer" aria-label="Open series page">
                          {team.series ?? '-'}
                        </a>
                      ) : (
                        team.series ?? '-'
                      )
                    ) : column.key === 'avgStamina' || column.key === 'avgForm' ? (
                      renderStatValue(team, column.key)
                    ) : (
                      team[column.key] ?? '-'
                    )}
                  </td>
                ))}
              </tr>
              {isHatstatsHistoryExpanded ? (
                <tr className="hatstatsHistoryRow">
                  <td colSpan={columns.length}>
                    <HatstatsHistoryChart history={team.hatstatsHistory} />
                  </td>
                </tr>
              ) : null}
              </React.Fragment>
              )
            })}
          </tbody>
        </table>
      )}
    </section>
  )
}

export default TeamsTable
